import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Crosshair, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { loadGoogleMapsPlaces } from "@/lib/google-maps";

export type PlaceSelection = {
  formattedAddress: string;
  placeId?: string;
  city?: string;
  country?: string;
  lat?: number;
  lng?: number;
};

function extractCityCountry(place: any): { city?: string; country?: string } {
  const comps: any[] = place?.address_components || [];
  const find = (type: string) => comps.find((c) => (c.types || []).includes(type))?.long_name;
  const city =
    find("locality") ||
    find("postal_town") ||
    find("administrative_area_level_2") ||
    find("administrative_area_level_1");
  const country = find("country");
  return { city, country };
}

export function PlacesAutocompleteInput({
  value,
  onValueChange,
  onPlaceSelected,
  placeholder,
  disabled,
  id,
  showUseMyLocation,
}: {
  value: string;
  onValueChange: (v: string) => void;
  onPlaceSelected?: (place: PlaceSelection) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  /** When true, show a button to fill the address from the user's current GPS location. */
  showUseMyLocation?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const acRef = useRef<any>(null);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  const canUsePlaces = useMemo(() => ready && !!(window as any).google?.maps?.places, [ready]);

  const emitSelection = useCallback(() => {
    const ac = acRef.current;
    if (!ac) return;
    const place = ac.getPlace?.();
    const formattedAddress = place?.formatted_address || inputRef.current?.value || "";
    if (formattedAddress) onValueChange(formattedAddress);

    const { city, country } = extractCityCountry(place);
    const lat = place?.geometry?.location?.lat?.();
    const lng = place?.geometry?.location?.lng?.();

    // Only fire place-selected when Google actually resolved a place.
    // (In dialogs, firing on blur while the user is still typing can cause the dialog to close.)
    const hasResolvedPlace = !!place?.place_id || (typeof lat === "number" && typeof lng === "number");
    if (hasResolvedPlace) {
      onPlaceSelected?.({
        formattedAddress,
        placeId: place?.place_id,
        city,
        country,
        lat: typeof lat === "number" ? lat : undefined,
        lng: typeof lng === "number" ? lng : undefined,
      });
    }
  }, [onPlaceSelected, onValueChange]);

  useEffect(() => {
    let mounted = true;
    loadGoogleMapsPlaces()
      .then(() => {
        if (!mounted) return;
        setReady(true);
      })
      .catch((err) => {
        if (!mounted) return;
        setLoadError(err instanceof Error ? err.message : "Failed to load Google Maps");
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!canUsePlaces) return;
    if (!inputRef.current) return;

    const google = (window as any).google;
    const ac = new google.maps.places.Autocomplete(inputRef.current, {
      fields: ["formatted_address", "place_id", "geometry", "address_components"],
      types: ["geocode"],
    });
    acRef.current = ac;

    const listener = ac.addListener("place_changed", () => {
      emitSelection();
    });

    return () => {
      if (listener?.remove) listener.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUsePlaces, emitSelection]);

  // Fallback: in some modal/overlay contexts the click on a suggestion can be swallowed
  // (the list disappears but place_changed never fires). When the suggestions close and
  // the input loses focus, we attempt to read the selected place/value and update state.
  const handleBlur = () => {
    // Defer so Google can finalize selection if it happened.
    setTimeout(() => emitSelection(), 0);
  };

  const useMyLocation = useCallback(() => {
    if (disabled || !navigator.geolocation) {
      setLocationError("Geolocation is not supported");
      return;
    }
    setLocationError(null);
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const pos = { lat: position.coords.latitude, lng: position.coords.longitude };
        const google = (window as any).google;
        if (!google?.maps?.Geocoder) {
          setLocationLoading(false);
          setLocationError("Maps not ready");
          return;
        }
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ location: pos }, (results: any, status: string) => {
          setLocationLoading(false);
          if (status !== "OK" || !results?.length) {
            setLocationError("Could not find address for this location");
            return;
          }
          const formatted = results[0]?.formatted_address;
          if (formatted) {
            onValueChange(formatted);
            onPlaceSelected?.({
              formattedAddress: formatted,
              placeId: results[0]?.place_id,
              lat: pos.lat,
              lng: pos.lng,
            });
          }
        });
      },
      (err) => {
        setLocationLoading(false);
        setLocationError(err.message || "Failed to get your location");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, [disabled, onValueChange, onPlaceSelected]);

  return (
    <div className="space-y-1">
      <div className={showUseMyLocation ? "flex gap-2" : undefined}>
        <div className={showUseMyLocation ? "flex-1 min-w-0" : undefined}>
          <Input
            ref={inputRef}
            id={id}
            value={value}
            onChange={(e) => onValueChange(e.target.value)}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
          />
        </div>
        {showUseMyLocation && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-10 w-10 flex-shrink-0"
            onClick={useMyLocation}
            disabled={disabled || locationLoading || !ready}
            title="Use my current location"
          >
            {locationLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Crosshair className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>
      {locationError && (
        <p className="text-xs text-destructive">{locationError}</p>
      )}
      {!locationError && loadError ? (
        <p className="text-xs text-muted-foreground">
          Autocomplete unavailable ({loadError}). You can still type the address manually.
        </p>
      ) : !locationError ? (
        <p className="text-xs text-muted-foreground">
          Start typing to select an address.
        </p>
      ) : null}
    </div>
  );
}


